import { CurrenciesContext } from '@/currencies/currency.context';
import { styled, SxProp } from 'dripsy';
import { useContext } from 'react';
import { Text } from 'react-native';

import { formatAmountByDecimals } from '../../../utils/numberFormatting';

export enum FONT_SIZE {
  H1 = 'H1', // fontSize: 60, lineHeight: 73
  H2 = 'H2', // fontSize: 48, lineHeight: 58.1
  H3 = 'H3', // fontSize: 32, lineHeight: 39
  H4 = 'H4', // fontSize: 28, lineHeight: 34
  H5 = 'H5', // fontSize: 24, lineHeight: 29
  H6 = 'H6', // fontSize: 20, lineHeight: 24
  textTitle = 'textTitle', // fontSize: 16, lineHeight: 19
  textBody = 'textBody', // fontSize: 14, lineHeight: 17
  textCaption = 'textCaption', // fontSize: 13, lineHeight: 15.7
  textSmall = 'textSmall' // fontSize: 12, lineHeight: 14.5
}

enum FONT_WEIGHT {
  bold = '700', // fontWeight: 700
  semiBold = '600', // fontWeight: 600
  regular = '400' // fontWeight: 400
}

enum COLOR {
  primary = '$textPrimary',
  secondary = '$textSecondary',
  success = '$success',
  danger = '$danger',
  warning = '$warning',
  info = '$info',
  hyperlink = '$hyperlink',
  themePrimary = '$primary',
  disabled = '$textDateDisabled'
}

export interface TextViewProps {
  bold?: boolean;
  semiBold?: boolean;
  secondary?: boolean;
  success?: boolean;
  danger?: boolean;
  warning?: boolean;
  info?: boolean;
  label?: boolean;
  hyperlink?: boolean;
  color?: string;
  themePrimary?: boolean;
  disabled?: boolean;
}

const getColor = (props: TextViewProps) => {
  if (props.secondary) {
    return COLOR.secondary;
  }
  if (props.success) {
    return COLOR.success;
  }
  if (props.danger) {
    return COLOR.danger;
  }
  if (props.warning) {
    return COLOR.warning;
  }
  if (props.info) {
    return COLOR.info;
  }
  if (props.hyperlink) {
    return COLOR.hyperlink;
  }
  if (props.themePrimary) {
    return COLOR.themePrimary;
  }
  if (props.disabled) {
    return COLOR.disabled;
  }
  return COLOR.primary;
};

const getMarginBottom = (props: 'label' | 'text') => {
  if (props === 'label') {
    return 4;
  }
  return 0;
};

const getTextSx = (props: TextViewProps) => {
  return {
    color: getColor(props),
    mb: getMarginBottom(props.label ? 'label' : 'text'),
    fontWeight: props.bold ? FONT_WEIGHT.bold : props.semiBold ? FONT_WEIGHT.semiBold : FONT_WEIGHT.regular,
    fontFamily: props.bold ? 'Poppins_700Bold' : props.semiBold ? 'Poppins_600SemiBold' : 'Poppins_400Regular'
  };
};

export const TextView = styled(Text, {
  themeKey: 'text',
  defaultVariant: 'textBody'
})(getTextSx);

// Text Small
export const TextSmall = styled(Text, {
  themeKey: 'text',
  defaultVariant: 'textSmall'
})(getTextSx);

// Text Caption
export const TextCaption = styled(Text, {
  themeKey: 'text',
  defaultVariant: 'textCaption'
})(getTextSx);

// Text Body
export const TextBody = styled(Text, {
  themeKey: 'text',
  defaultVariant: 'textBody'
})(getTextSx);

// Text Title
export const TextTitle = styled(Text, {
  themeKey: 'text',
  defaultVariant: 'textTitle'
})(getTextSx);

// H6
export const H6 = styled(Text, {
  themeKey: 'text',
  defaultVariant: 'H6'
})(getTextSx);

// H5
export const H5 = styled(Text, {
  themeKey: 'text',
  defaultVariant: 'H5'
})(getTextSx);

// H4
export const H4 = styled(Text, {
  themeKey: 'text',
  defaultVariant: 'H4'
})(getTextSx);

// H3
export const H3 = styled(Text, {
  themeKey: 'text',
  defaultVariant: 'H3'
})(getTextSx);

// H2
export const H2 = styled(Text, {
  themeKey: 'text',
  defaultVariant: 'H2'
})(getTextSx);

// H1
export const H1 = styled(Text, {
  themeKey: 'text',
  defaultVariant: 'H1'
})(getTextSx);

export const TextAmount = (
  props: {
    children: string | number;
    size?: keyof typeof FONT_SIZE;
    weight?: keyof typeof FONT_WEIGHT;
    decimals?: number;
    currency?: string;
    prefix?: string;
    shouldHide?: boolean;
    label?: boolean;
    sx?: SxProp;
  } & TextViewProps
) => {
  const {
    children,
    sx,
    label = false,
    size = FONT_SIZE.textBody,
    weight = 'regular',
    decimals = 6,
    currency = '',
    prefix = '',
    shouldHide = false
  } = props;
  const { getDecimalByAsset } = useContext(CurrenciesContext);
  let formattedAmount = '*********';

  if (!shouldHide) {
    formattedAmount =
      formatAmountByDecimals({
        value: children,
        decimals: getDecimalByAsset(currency) || 6,
        keepZero: true
      }) || ' - ';
  }

  return (
    <TextView
      variants={[FONT_SIZE[size]]}
      sx={{ color: getColor(props), mb: label ? '$1' : '$0', ...sx }}
      bold={weight === 'bold'}
      semiBold={weight === 'semiBold'}>
      {prefix}
      {formattedAmount}
      {currency ? ` ${currency}` : ''}
    </TextView>
  );
};
